'use client'

import { useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { trackEvent } from '@/lib/analytics'
import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'
import { computeTotals, type ComputedTotals } from '@/lib/grass-comparison-scoring'
import { getRegionFromZipCode } from '@/data/regional-climate'
import { useSavedInputs } from '@/hooks/use-saved-inputs'

// Reads the current `?zip=` query param from window.location and validates it.
// Lives outside the hook so the same logic powers initial-mount sync, popstate
// re-reads, and post-submit URL updates without bringing in useSearchParams
// (which would force a Suspense boundary and leak a hidden streaming-template
// DOM copy). `raw` distinguishes "no zip param" from "invalid zip param" —
// an explicit-but-invalid URL zip must not fall back to the saved zip.
function readZipParam(): { raw: string | null; zip: string | null } {
  if (typeof window === 'undefined') return { raw: null, zip: null }
  const raw = new URLSearchParams(window.location.search).get('zip')
  if (!raw) return { raw: null, zip: null }
  const trimmed = raw.trim()
  if (!/^\d{5}$/.test(trimmed) || getRegionFromZipCode(trimmed) === 'Unknown') {
    return { raw, zip: null }
  }
  return { raw, zip: trimmed }
}

/** Appends `&zip=` to an href that already has a query string. Pure so the
 * compare-page tool links can be tested without rendering the page. */
export function appendZip(href: string, zip: string | null): string {
  if (!zip) return href
  return `${href}${href.includes('?') ? '&' : '?'}zip=${zip}`
}

export type ZipPrefillSource = 'url' | 'saved' | null

interface UseCompareZipOptions {
  /** False on brand-kind compare pages: the hook stays inert. */
  enabled: boolean
  grassNameA: string
  grassNameB: string
  displayNameA: string
  displayNameB: string
  slug: string
}

export interface CompareZipState {
  zip: string | null
  region: string | null
  totals: ComputedTotals | null
  /** 0 = A wins, 1 = B wins, null = no zip or tie. */
  winnerIdx: 0 | 1 | null
  tied: boolean
  prefillSource: ZipPrefillSource
  submitZip: (raw: string) => void
}

/**
 * Page-level ZIP state for /compare/[slug]. The server render is always the
 * "no zip" state (zip starts null) so SSG HTML stays identical for every
 * visitor; personalization appears via post-mount state updates only.
 * Resolution priority on mount: URL `?zip=` > saved inputs (localStorage).
 * A present-but-invalid URL zip wins over saved (renders unpersonalized).
 */
export function useCompareZip({
  enabled,
  grassNameA,
  grassNameB,
  displayNameA,
  displayNameB,
  slug,
}: UseCompareZipOptions): CompareZipState {
  const router = useRouter()
  const pathname = usePathname()
  const { savedInputs, saveInputs, clearInputs } = useSavedInputs()
  const [zip, setZip] = useState<string | null>(null)
  const [prefillSource, setPrefillSource] = useState<ZipPrefillSource>(null)
  const [, startTransition] = useTransition()
  // Once the URL has been checked or the user has submitted / navigated,
  // saved inputs (which load asynchronously from localStorage) must not
  // clobber state. urlHadParam blocks saved fallback when the URL carried an
  // explicit (even invalid) zip.
  const urlChecked = useRef(false)
  const urlHadParam = useRef(false)
  const userInteracted = useRef(false)
  const prefillTracked = useRef(false)

  useEffect(() => {
    if (!enabled || urlChecked.current) return
    urlChecked.current = true
    const { raw, zip: fromUrl } = readZipParam()
    urlHadParam.current = raw !== null
    if (fromUrl) {
      setZip(fromUrl)
      setPrefillSource('url')
      if (!prefillTracked.current) {
        prefillTracked.current = true
        trackEvent('zip_verdict_prefilled', { zip: fromUrl, source: 'url', slug })
      }
    }
  }, [enabled, slug])

  // Back/Forward (and bfcache restores) can change `?zip=` without remounting
  // the page — re-read the URL so the verdict never shows a stale ZIP. An
  // explicit navigation counts as user intent: block later saved-inputs fills.
  useEffect(() => {
    if (!enabled) return
    const onPopState = () => {
      userInteracted.current = true
      const { raw, zip: fromUrl } = readZipParam()
      urlHadParam.current = raw !== null
      setZip(fromUrl)
      setPrefillSource(fromUrl ? 'url' : null)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [enabled])

  // Saved-inputs prefill: fires after useSavedInputs' own mount effect
  // hydrates from localStorage, so it must be a separate reactive effect.
  // Skipped when the URL carried any zip param (valid or not) or the user
  // has already typed/navigated.
  useEffect(() => {
    if (!enabled || !urlChecked.current || urlHadParam.current || userInteracted.current) return
    if (zip || !savedInputs.zip) return
    const saved = savedInputs.zip.trim()
    if (!/^\d{5}$/.test(saved) || getRegionFromZipCode(saved) === 'Unknown') return
    setZip(saved)
    setPrefillSource('saved')
    if (!prefillTracked.current) {
      prefillTracked.current = true
      trackEvent('zip_verdict_prefilled', { zip: saved, source: 'saved', slug })
    }
  }, [enabled, savedInputs.zip, zip, slug])

  const submitZip = useCallback(
    (raw: string) => {
      if (!enabled) return
      userInteracted.current = true
      const trimmed = raw.trim()
      const current = new URLSearchParams(window.location.search)
      let nextZip: string | null = null
      if (/^\d{5}$/.test(trimmed) && getRegionFromZipCode(trimmed) !== 'Unknown') {
        current.set('zip', trimmed)
        nextZip = trimmed
      } else {
        current.delete('zip')
      }
      setZip(nextZip)
      setPrefillSource(null)

      if (nextZip) {
        saveInputs({ zip: nextZip, source: 'compare' })
        // Track the submission only when a valid ZIP produced a region verdict.
        const submittedRegion = getRegionFromZipCode(nextZip)
        const ga = GRASS_CHARACTERISTICS[grassNameA]
        const gb = GRASS_CHARACTERISTICS[grassNameB]
        if (ga && gb && submittedRegion && submittedRegion !== 'Unknown') {
          const t = computeTotals([ga, gb], nextZip)
          let verdictLabel: string
          if (t.noClimateCompatibleWinner) {
            verdictLabel = `neither grass fits ${submittedRegion}`
          } else if (t.tied) {
            verdictLabel = `tied — both suit ${submittedRegion}`
          } else {
            const winnerName = t.winnerIdx === 0 ? displayNameA : displayNameB
            verdictLabel = `${winnerName} wins for ${submittedRegion}`
          }
          trackEvent('zip_verdict_submitted', { zip: nextZip, verdict: verdictLabel })
        }
      } else if (trimmed === '') {
        // Deliberate clear: forget the persisted zip too, or it silently
        // reappears on the next zip-less visit.
        clearInputs(['zip'])
      }

      const qs = current.toString()
      startTransition(() => {
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false })
      })
    },
    [enabled, grassNameA, grassNameB, displayNameA, displayNameB, pathname, router, saveInputs, clearInputs]
  )

  const grassA = enabled ? GRASS_CHARACTERISTICS[grassNameA] : undefined
  const grassB = enabled ? GRASS_CHARACTERISTICS[grassNameB] : undefined

  const { region, totals } = useMemo(() => {
    if (!zip || !grassA || !grassB) return { region: null, totals: null }
    const r = getRegionFromZipCode(zip)
    if (!r || r === 'Unknown') return { region: null, totals: null }
    return { region: r, totals: computeTotals([grassA, grassB], zip) }
  }, [zip, grassA, grassB])

  const winnerIdx: 0 | 1 | null =
    totals && !totals.tied && (totals.winnerIdx === 0 || totals.winnerIdx === 1)
      ? (totals.winnerIdx as 0 | 1)
      : null

  return {
    zip: totals ? zip : null,
    region,
    totals,
    winnerIdx,
    tied: totals?.tied ?? false,
    prefillSource,
    submitZip,
  }
}
